import { ServiceResponse } from '@nx-smartmonkey/shared/hooks';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';

import { getFlatProjectsMutationKey } from '.';
import { projectService } from '../../application/ProjectService';
import * as routing from '../../constants/Routing';
import { ProjectActionType } from '../../context/project/actions';
import { useProjectContext } from '../../context/project/context';
import { FlatProject } from '../../domain/project/FlatProject';
import { Project } from '../../domain/project/Project';
import { User } from '../../domain/user/User';
import useErrorMiddleware from '../errorMiddleware/useErrorMiddleware';
import { useRetrieveOrganization } from '../organization/useRetrieveOrganization';
import { useShowSnackbarMessage } from '../snackbarMessage/useShowSnackbarMessage';
import { useCustomParams } from '../useCustomParams';

type UseRetrieveProjectsResponse = ServiceResponse<Function, FlatProject[]> & {
  activeProject?: Project;
};

// Hooks act as 'Adapter' layer.
export const useRetrieveFlatProjects = ({
  user,
  projectId,
}: {
  user?: User;
  projectId?: string;
}): UseRetrieveProjectsResponse => {
  const navigate = useNavigate();
  const { showSnackbarMessage } = useShowSnackbarMessage();
  const { data: organization } = useRetrieveOrganization();
  const { projectState, projectDispatch } = useProjectContext();
  const { sessionExpiredLogout } = useErrorMiddleware();
  const { value: paramsProjectId } = useCustomParams({ param: `projectId` });

  const {
    isInitialLoading: isLoading,
    refetch,
    isError,
    isSuccess,
  } = useQuery(getFlatProjectsMutationKey(), () => projectService.fetchAllFlat(), {
    enabled: !!user && !!organization,
    retry: false,
    staleTime: Infinity,
    refetchOnWindowFocus: false,
    onSuccess: (flatProjects) => {
      const activeProjectId = paramsProjectId ?? projectId ?? user?.default_project_id ?? flatProjects[0].id;

      if (flatProjects.map((project) => project.id).includes(activeProjectId)) {
        projectDispatch({
          type: ProjectActionType.FETCH_PROJECTS_SUCCESS,
          payload: {
            flatProjects,
          },
        });
      } else {
        navigate(routing.PRIVATE_HIGHWAY);
      }
    },
    onError: (err: any) => {
      console.info(`Planner Retrieve Projects Error: `, err?.response);

      if (err?.response?.status === 401) {
        sessionExpiredLogout();
        return;
      }

      showSnackbarMessage({
        messageIntlId: `infoerror.${err?.response?.data?.messageId ?? `network_error`}`,
      });
    },
  });

  return {
    error: isError,
    isLoading,
    success: isSuccess,
    run: refetch,
    data: projectState.flatProjects,
    activeProject: projectState.activeProject,
  };
};
